/**
 * Validation centralisée pour les formulaires de projets
 * Utilisé par NewProjectForm et AdminProjectEditingModal
 */

/**
 * Interface pour les données du formulaire de projet
 * Les champs numériques acceptent '' pour permettre les champs vides
 */
export interface ProjectFormData {
  name: string;
  description: string;
  contribution: number | '';
  startDate: any;
  dueDay: number | '';
  withdrawalDay: number | '';
  cashFundAmount: number | '';
  penaltyAmountPerDay: number | '';
}

/**
 * Interface pour les erreurs du formulaire de projet
 */
export interface ProjectFormErrors {
  name: string;
  description: string;
  contribution: string;
  startDate: string;
  dueDay: string;
  withdrawalDay: string;
  cashFundAmount: string;
  penaltyAmountPerDay: string;
}

/**
 * Valide les données d'un formulaire de projet (création ou édition)
 *
 * @param data - Les données du formulaire à valider
 * @returns Un objet avec les erreurs par champ (string vide si valide)
 *
 * @example
 * const errors = validateProjectForm({
 *   name: "Mon Projet",
 *   description: "Description du projet",
 *   contribution: 1000,
 *   startDate: dayjs(),
 *   dueDay: 5,
 *   withdrawalDay: 10,
 *   cashFundAmount: 500,
 *   penaltyAmountPerDay: 10
 * });
 *
 * if (Object.values(errors).some(err => err !== '')) {
 *   setFormErrors(errors);
 *   return;
 * }
 */
export const validateProjectForm = (data: ProjectFormData): ProjectFormErrors => {
  const contribution = data.contribution === '' ? 0 : data.contribution;
  const dueDay = data.dueDay === '' ? 0 : data.dueDay;
  const withdrawalDay = data.withdrawalDay === '' ? 0 : data.withdrawalDay;
  const cashFundAmount = data.cashFundAmount === '' ? 0 : data.cashFundAmount;
  const penaltyAmountPerDay = data.penaltyAmountPerDay === '' ? 0 : data.penaltyAmountPerDay;

  return {
    name: data.name.trim() === '' ? 'Le nom du projet est requis' : '',
    description: data.description.trim() === '' ? 'La description est requise' : '',
    contribution: contribution <= 0 ? 'La contribution doit être supérieure à 0' : '',
    startDate: !data.startDate ? 'La date de début est requise' : '',
    dueDay: (dueDay < 1 || dueDay > 31) ? 'Jour invalide (1-31)' : '',
    withdrawalDay: (withdrawalDay < 1 || withdrawalDay > 31) ? 'Jour invalide (1-31)' : '',
    cashFundAmount: (cashFundAmount < 0 || cashFundAmount > 1000000)
      ? 'Montant invalide (0-1000000)'
      : '',
    penaltyAmountPerDay: penaltyAmountPerDay < 0 ? 'Montant invalide' : ''
  };
};

/**
 * Vérifie si le formulaire est valide (tous les champs requis remplis)
 * Utile pour activer/désactiver le bouton de soumission
 *
 * @param data - Les données du formulaire
 * @returns true si le formulaire est valide, false sinon
 */
export const isProjectFormValid = (data: ProjectFormData): boolean => {
  const contribution = data.contribution === '' ? 0 : data.contribution;
  const dueDay = data.dueDay === '' ? 0 : data.dueDay;
  const withdrawalDay = data.withdrawalDay === '' ? 0 : data.withdrawalDay;
  const cashFundAmount = data.cashFundAmount === '' ? 0 : data.cashFundAmount;
  const penaltyAmountPerDay = data.penaltyAmountPerDay === '' ? 0 : data.penaltyAmountPerDay;

  return (
    data.name.trim() !== '' &&
    data.description.trim() !== '' &&
    contribution > 0 &&
    data.startDate &&
    dueDay >= 1 && dueDay <= 31 &&
    withdrawalDay >= 1 && withdrawalDay <= 31 &&
    cashFundAmount >= 0 && cashFundAmount <= 1000000 &&
    penaltyAmountPerDay >= 0
  );
};